/**
 * Client onboarding wizard (Documents Phase 2).
 *
 * "New from document" on the Clients page: a trade license upload lands in a
 * STAGING area (onboarding_documents — never the vault), extraction prefills
 * the client create form, and completing the wizard atomically creates the
 * client and moves the file into the client's vault as CONFIRMED. Tiers 2/3
 * (VAT / corporate tax certificates) stage then attach onto the new client.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema } from './common';
import {
  clientDocumentTypeSchema,
  confirmableFieldSchema,
  dateOnlySchema,
  extractedFieldsSchema,
} from './client-document';
import { createClientInputSchema } from './client';
import { taxReviewFieldsSchema } from './tax';

export const onboardingDocumentStatusSchema = z.enum(['PENDING', 'CONSUMED']);
export type OnboardingDocumentStatus = z.infer<typeof onboardingDocumentStatusSchema>;

export const onboardingDocumentOutputSchema = z.object({
  id: idSchema,
  type: clientDocumentTypeSchema,
  status: onboardingDocumentStatusSchema,
  originalName: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
  extracted: extractedFieldsSchema.nullable(),
  extractionError: z.string().nullable(),
  uploadedById: idSchema.nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type OnboardingDocumentOutput = z.infer<typeof onboardingDocumentOutputSchema>;

/** An existing client whose TRN or trade license no. matches extracted data. */
export const duplicateClientMatchSchema = z.object({
  id: idSchema,
  clientCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  matchedOn: z.array(z.enum(['trn', 'tradeLicenseNo'])),
  hidden: z.boolean().optional(),
});
export type DuplicateClientMatch = z.infer<typeof duplicateClientMatchSchema>;

export const onboardingUploadOutputSchema = z.object({
  document: onboardingDocumentOutputSchema,
  duplicates: z.array(duplicateClientMatchSchema),
});
export type OnboardingUploadOutput = z.infer<typeof onboardingUploadOutputSchema>;

/** Tier 1 — atomic create-client-from-document. `client` is the reviewed form. */
export const completeOnboardingInputSchema = z.object({
  stagingDocumentId: idSchema,
  /** Required true to proceed when `duplicates` were flagged. */
  allowDuplicate: z.boolean().default(false),
  client: createClientInputSchema,
});
export type CompleteOnboardingInput = z.infer<typeof completeOnboardingInputSchema>;

/** Tiers 2/3 — attach a staged document onto a client as CONFIRMED. */
export const attachOnboardingInputSchema = z.object({
  stagingDocumentId: idSchema,
  clientId: idSchema,
  /** Reviewer-vetted values; null/absent = do not write. */
  fields: z.object({
    legalName: z.string().max(300).nullable().optional(),
    tradeLicenseNo: z.string().max(100).nullable().optional(),
    trn: z.string().max(50).nullable().optional(),
    incorporationDate: dateOnlySchema.nullable().optional(),
    jurisdiction: z.string().nullable().optional(),
    fyEnd: dateOnlySchema.nullable().optional(),
  }).default({}),
  applyConflicts: z.array(confirmableFieldSchema).default([]),
  taxFields: taxReviewFieldsSchema.optional(),
});
export type AttachOnboardingInput = z.infer<typeof attachOnboardingInputSchema>;

const taxEvidenceSchema = z.object({
  documentId: idSchema,
  effectiveDate: dateOnlySchema.nullable().default(null),
  issueDate: dateOnlySchema.nullable().default(null),
  firstPeriodStartDate: dateOnlySchema.nullable().default(null),
  firstPeriodEndDate: dateOnlySchema.nullable().default(null),
  firstReturnDueDate: dateOnlySchema.nullable().default(null),
  taxPeriodPattern: z.string().nullable().default(null),
  deadline: dateOnlySchema.nullable(),
  deadlineSource: z.enum(['PRINTED', 'DERIVED', 'UNKNOWN']).default('UNKNOWN'),
  derivedDeadline: dateOnlySchema.nullable().default(null),
  warnings: z.array(z.string()).default([]),
});
const registryPairSchema = z.object({ endDate: dateOnlySchema.nullable(), dueDate: dateOnlySchema.nullable() });
export const ukRegistryEvidenceSchema = z.object({
  companyNumber: z.string().regex(/^[A-Z0-9]{8}$/),
  sourceUrl: z.string().url(),
  fetchedAt: z.string().datetime().nullable(),
  accounts: registryPairSchema,
  confirmationStatement: registryPairSchema,
  hmrcInternalTarget: registryPairSchema,
  warnings: z.array(z.string()).default([]),
});
export type UkRegistryEvidence = z.infer<typeof ukRegistryEvidenceSchema>;
export const templateEvidenceSchema = z.object({
  vat: taxEvidenceSchema.nullable(),
  corporateTax: taxEvidenceSchema.extend({ fyEnd: dateOnlySchema.nullable() }).nullable(),
  tradeLicense: z.object({ documentId: idSchema, expiryDate: dateOnlySchema.nullable() }).nullable(),
  ukRegistry: ukRegistryEvidenceSchema.nullable().optional(),
});
export type TemplateEvidence = z.infer<typeof templateEvidenceSchema>;
